import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, Phone, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtUSD } from "@/lib/api";

export default function BookingPendingPage() {
    const { bookingId } = useParams();
    const [booking, setBooking] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!bookingId) return;
        api.get(`/bookings/${bookingId}`)
            .then((r) => setBooking(r.data))
            .catch(() =>
                setErr("We couldn't find that booking. Please call 340-423-6611."),
            );
    }, [bookingId]);

    if (err) {
        return (
            <div className="pt-40 pb-24 max-w-2xl mx-auto px-6 text-center">
                <h1 className="font-serif text-4xl text-forest-700">Hmm.</h1>
                <p className="text-forest-700/60 mt-3">{err}</p>
            </div>
        );
    }

    if (!booking) {
        return (
            <div
                className="pt-40 pb-24 max-w-2xl mx-auto px-6 text-center"
                data-testid="pending-loading"
            >
                <Loader2 className="w-8 h-8 mx-auto animate-spin text-forest-700" />
                <p className="text-forest-700/60 mt-4">Preparing your invoice…</p>
            </div>
        );
    }

    const copyInvoice = () => {
        navigator.clipboard?.writeText(booking.invoice_number);
        toast.success("Invoice number copied.");
    };

    const q = booking.quote || {};

    return (
        <div data-testid="booking-pending-page" className="pt-28 lg:pt-32 pb-24">
            <div className="max-w-2xl mx-auto px-6 lg:px-10">
                {/* Success header */}
                <div className="text-center mb-12">
                    <div className="w-14 h-14 mx-auto rounded-full bg-forest-400/20 flex items-center justify-center">
                        <Check className="w-7 h-7 text-forest-700" strokeWidth={1.5} />
                    </div>
                    <span className="overline mt-6 inline-block">
                        Reservation received
                    </span>
                    <h1 className="font-serif font-light text-4xl md:text-5xl text-forest-700 mt-4 leading-[1.05]">
                        Call us to <em className="italic">confirm.</em>
                    </h1>
                </div>

                {/* Compact invoice card — three essentials */}
                <div
                    className="bg-forest-700 text-sand-100 rounded-sm p-8 lg:p-10"
                    data-testid="invoice-card"
                >
                    <div className="text-center">
                        <div className="overline !text-palm-400">Invoice</div>
                        <div className="flex items-center justify-center gap-3 mt-3">
                            <div
                                className="font-serif text-4xl md:text-5xl tracking-wider"
                                data-testid="invoice-number"
                            >
                                {booking.invoice_number}
                            </div>
                            <button
                                onClick={copyInvoice}
                                data-testid="copy-invoice-btn"
                                className="w-9 h-9 rounded-full bg-sand-100/10 hover:bg-sand-100/25 border border-sand-100/25 flex items-center justify-center"
                                aria-label="Copy invoice number"
                            >
                                <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />
                            </button>
                        </div>
                    </div>

                    <div className="mt-10 pt-8 border-t border-sand-100/15 text-center">
                        <div className="overline !text-palm-400">Total</div>
                        <div
                            className="font-serif text-5xl md:text-6xl mt-3 text-emerald-400"
                            data-testid="invoice-total"
                        >
                            {fmtUSD(q.total)}
                        </div>
                    </div>

                    <div className="mt-10 pt-8 border-t border-sand-100/15 text-center">
                        <div className="overline !text-palm-400">Call to pay</div>
                        <a
                            href="tel:3404236611"
                            data-testid="call-erin-btn"
                            className="mt-3 inline-flex items-center gap-3 font-serif text-4xl md:text-5xl tracking-tight text-sand-100 hover:text-white transition-colors"
                        >
                            <Phone className="w-7 h-7" strokeWidth={1.5} />
                            340-423-6611
                        </a>
                    </div>
                </div>

                <p className="text-center text-xs text-forest-700/55 mt-8 leading-relaxed max-w-md mx-auto">
                    Have your invoice number ready. Your reservation is confirmed
                    once we've collected the 50% deposit by phone.
                </p>

                <div className="text-center mt-10">
                    <Link
                        to="/"
                        data-testid="back-home-link"
                        className="text-sm text-forest-700 underline-link"
                    >
                        Back to home
                    </Link>
                </div>
            </div>
        </div>
    );
}
